import { NavigationType } from 'react-router-dom';

export type ScrollIntent =
  | { kind: 'top' | 'restore' }
  | { kind: 'hash'; id: string };

export type ScrollIntentInput = {
  navigationType: NavigationType;
  pathname: string;
  hash: string;
  /** Undefined on a fresh load or a reload. */
  previousPathname?: string;
  previousOriginPathname?: string;
  previousIntent?: ScrollIntent;
  declaredReturn?: boolean;
};

export const resolveScrollIntent = ({
  navigationType,
  pathname,
  hash,
  previousPathname,
  previousOriginPathname,
  previousIntent,
  declaredReturn,
}: ScrollIntentInput): ScrollIntent => {
  const isFirstLoad = previousPathname === undefined;

  // Going back to a deep-linked page restores, not re-jumps to the anchor
  if (navigationType === NavigationType.Pop && !isFirstLoad) {
    return { kind: 'restore' };
  }

  if (navigationType === NavigationType.Replace && previousIntent) {
    return previousIntent;
  }

  if (hash.length > 1) return { kind: 'hash', id: hash.slice(1) };

  // History state survives a reload, so a reloaded return is still a reload
  if (isFirstLoad) return { kind: 'restore' };

  if (declaredReturn) {
    return { kind: pathname === previousOriginPathname ? 'restore' : 'top' };
  }

  return { kind: 'top' };
};

export const SCROLL_RETURN_STATE = { scroll: 'return' } as const;

const isScrollReturnState = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  (state as { scroll?: unknown }).scroll === SCROLL_RETURN_STATE.scroll;

type Navigation = {
  key: string;
  navigationType: NavigationType;
  pathname: string;
  hash: string;
  state: unknown;
};

type Entry = { pathname: string; originPathname?: string };

/**
 * Remembers, per history entry, which page it was entered from: history the
 * router doesn't keep, but a breadcrumb return needs.
 */
export const createScrollIntentTracker = () => {
  const entries = new Map<string, Entry>();
  let current: { key: string; entry: Entry; intent: ScrollIntent } | undefined;

  const track = ({
    key,
    navigationType,
    pathname,
    hash,
    state,
  }: Navigation): ScrollIntent => {
    if (current?.key === key) return current.intent;

    const previous = current;
    const known = entries.get(key);
    // A replaced entry takes the place of the one before it, origin included
    const originPathname = known
      ? known.originPathname
      : navigationType === NavigationType.Replace
        ? previous?.entry.originPathname
        : previous?.entry.pathname;

    const entry = { pathname, originPathname };
    entries.set(key, entry);

    const intent = resolveScrollIntent({
      navigationType,
      pathname,
      hash,
      previousPathname: previous?.entry.pathname,
      previousOriginPathname: previous?.entry.originPathname,
      previousIntent: previous?.intent,
      declaredReturn: isScrollReturnState(state),
    });

    current = { key, entry, intent };
    return intent;
  };

  return { track };
};
